import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem,
  IconButton, Collapse, Chip, Button, Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { sendServerRequest } from '../../../server/request';
import { openGlobalSnack } from '../../utils/snack';
import { CircularLoading } from '../../utils/loading';

const ENDPOINT = '/server/fgtsCommand/errorLogs';
const PAGE_SIZE = 20;

type ErrorLog = {
  id: string;
  type: string;
  provider: string | null;
  cpf: string | null;
  client_id: number | null;
  chat_id: number | null;
  status: number | null;
  message: string;
  response: string | null;
  path: string | null;
  body: string | null;
  createdAt: string;
};

const TYPE_LABELS: Record<string, { label: string; color: 'info' | 'warning' | 'default' }> = {
  consult: { label: 'Consulta', color: 'info' },
  proposal: { label: 'Proposta', color: 'warning' },
};

function typeChip(type: string) {
  return TYPE_LABELS[type] ?? { label: type, color: 'default' as const };
}

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso; }
}

function prettyJson(raw: string | null) {
  if (!raw) return '';
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

export function FgtsErrorLogsPanel() {
  const [logs, setLogs] = useState<ErrorLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [type, setType] = useState('');
  const [provider, setProvider] = useState('');
  const [cpf, setCpf] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function load(toPage: number) {
    setLoading(true);
    const q = new URLSearchParams({ page: String(toPage), pageSize: String(PAGE_SIZE) });
    if (type) q.set('type', type);
    if (provider.trim()) q.set('provider', provider.trim());
    if (cpf.trim()) q.set('cpf', cpf.trim());
    sendServerRequest('GET', `${ENDPOINT}/list?${q.toString()}`)
      .then((e) => {
        const data = e.data?.data ?? {};
        setLogs(data.logs ?? []);
        setTotal(data.total ?? 0);
        setPage(toPage);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar os logs de erro.', 'error');
      });
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <h3 style={{ marginBottom: 4, textAlign: 'center' }}>Logs de erro (FGTS)</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16, textAlign: 'center' }}>
        Erros nas consultas de saldo e na criação de propostas FGTS, por banco. Apagados automaticamente após 30 dias.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <TextField select size="small" label="Tipo" value={type} style={{ minWidth: 140 }}
          onChange={(e) => setType(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="consult">Consulta</MenuItem>
          <MenuItem value="proposal">Proposta</MenuItem>
        </TextField>
        <TextField size="small" label="Banco" value={provider} style={{ minWidth: 140 }}
          onChange={(e) => setProvider(e.target.value)} />
        <TextField size="small" label="CPF" value={cpf} onChange={(e) => setCpf(e.target.value)} />
        <Button variant="contained" onClick={() => load(1)}>Filtrar</Button>
        <IconButton onClick={() => load(page)}><RefreshIcon /></IconButton>
      </div>

      {loading ? <CircularLoading /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Data</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Banco</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Mensagem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" style={{ color: '#00000080' }}>Nenhum log.</TableCell></TableRow>
              )}
              {logs.map((l) => (
                <React.Fragment key={l.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
                        {expanded === l.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.createdAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={typeChip(l.type).label}
                        color={typeChip(l.type).color} variant="outlined" />
                    </TableCell>
                    <TableCell>{l.provider ?? '—'}</TableCell>
                    <TableCell>{l.cpf ?? '—'}</TableCell>
                    <TableCell>{l.status ?? '—'}</TableCell>
                    <TableCell style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.message}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} style={{ paddingTop: 0, paddingBottom: 0, border: 0 }}>
                      <Collapse in={expanded === l.id} unmountOnExit>
                        <div style={{ padding: 12, background: '#00000008' }}>
                          <div style={{ fontSize: 12, marginBottom: 8 }}>
                            <b>client_id:</b> {l.client_id ?? '—'} &nbsp;|&nbsp;
                            <b>chat_id:</b> {l.chat_id ?? '—'}
                          </div>
                          <div style={{ fontSize: 12, marginBottom: 4 }}><b>Mensagem:</b> {l.message}</div>
                          {l.path && (
                            <div style={{ fontSize: 12, marginBottom: 4 }}>
                              <b>Endpoint:</b>{' '}
                              <code style={{ background: '#0000000a', padding: '1px 4px', borderRadius: 3, wordBreak: 'break-all' }}>{l.path}</code>
                            </div>
                          )}
                          {l.body && (
                            <>
                              <b style={{ fontSize: 12 }}>Body enviado:</b>
                              <pre style={{
                                fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                                maxHeight: 200, overflow: 'auto', margin: '4px 0 8px', background: '#0000000a', padding: 8, borderRadius: 4,
                              }}>{prettyJson(l.body)}</pre>
                            </>
                          )}
                          <b style={{ fontSize: 12 }}>Resposta do banco:</b>
                          <pre style={{
                            fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                            maxHeight: 260, overflow: 'auto', margin: '4px 0 0', background: '#0000000a', padding: 8, borderRadius: 4,
                          }}>{prettyJson(l.response) || '—'}</pre>
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <Pagination count={pages} page={page} onChange={(_, p) => load(p)} />
        </div>
      )}
    </div>
  );
}
